import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PaperPlaneRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { IMAGES } from "../data/content";
import { api, formatApiError } from "../lib/api";

const INITIAL = { name: "", email: "", phone: "", ownership_status: "", interest: "", brand: "", resort: "", message: "" };

const inputCls =
  "w-full bg-transparent border border-border rounded-sm px-4 py-3.5 text-sm font-medium text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary transition-colors duration-300";

export default function Consult() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const applyPlan = (note) => setForm((f) => (f.message ? f : { ...f, message: note }));
    const stored = sessionStorage.getItem("preferred_plan");
    if (stored) {
      sessionStorage.removeItem("preferred_plan");
      applyPlan(stored);
    }
    const onPlan = (e) => applyPlan(e.detail);
    window.addEventListener("preferred-plan", onPlan);
    return () => window.removeEventListener("preferred-plan", onPlan);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, referral_code: sessionStorage.getItem("referral_code") || "" });
      setSent(true);
      setForm(INITIAL);
      toast.success("Thank you — we'll be in touch within one business day.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="consult" data-testid="consult-section" className="py-24 md:py-40 bg-secondary/40 border-t border-border">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-5">
          <div ref={imgRef} className="overflow-hidden rounded-sm border border-border aspect-[4/5] max-w-md">
            <motion.img
              src={IMAGES.consult}
              alt="A couple reviewing their options together"
              style={{ scale }}
              className="w-full h-full object-cover"
              data-testid="consult-image"
            />
          </div>
          <blockquote className="mt-8 max-w-md">
            <p className="font-serif italic text-xl md:text-2xl text-primary leading-snug">
              "The best decision is the one you make slowly, with the numbers in front of you."
            </p>
            <cite className="not-italic block mt-3 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              The Timeshare Edit
            </cite>
          </blockquote>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <Eyebrow>Free Consultation</Eyebrow>
          <h2 className="font-serif text-primary tracking-tight text-4xl sm:text-5xl mt-6 leading-[1.08]" data-testid="consult-heading">
            Talk it through before you sign anything
          </h2>
          <p className="mt-5 text-foreground/70 max-w-xl">
            Tell us where you stand. We'll come back with honest, no-pressure direction on buying more,
            selling, or choosing a company to work with.
          </p>

          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-10 bg-card border border-border rounded-sm p-10 flex items-start gap-5"
              data-testid="consult-success"
            >
              <CheckCircle size={36} weight="fill" className="text-primary shrink-0" />
              <div>
                <h3 className="font-serif text-2xl text-primary">Request received</h3>
                <p className="mt-2 text-foreground/70 text-sm md:text-base">
                  Thank you for reaching out. A real person will review your situation and reply within one business day.
                </p>
                <button
                  data-testid="consult-send-another"
                  onClick={() => setSent(false)}
                  className="mt-5 text-sm font-semibold text-accent border-b-2 border-accent pb-0.5 hover:text-primary hover:border-primary transition-colors duration-300"
                >
                  Send another enquiry
                </button>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="consult-form">
              <input data-testid="consult-name" required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="consult-email" required type="email" placeholder="Email address *" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="consult-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} />
              <input data-testid="consult-brand" placeholder="Ownership brand (e.g. Marriott, Hilton)" value={form.brand} onChange={set("brand")} className={inputCls} />
              <input data-testid="consult-resort" placeholder="Resort / developer (optional)" value={form.resort} onChange={set("resort")} className={inputCls} />
              <select data-testid="consult-ownership-status" required value={form.ownership_status} onChange={set("ownership_status")} className={`${inputCls} ${!form.ownership_status ? "text-foreground/35" : ""}`}>
                <option value="" disabled>I currently… *</option>
                <option value="owner">Own a vacation ownership</option>
                <option value="considering">Am considering buying</option>
                <option value="exiting">Own and want to exit</option>
              </select>
              <select data-testid="consult-interest" required value={form.interest} onChange={set("interest")} className={`${inputCls} ${!form.interest ? "text-foreground/35" : ""}`}>
                <option value="" disabled>I'm interested in… *</option>
                <option value="buying">Buying more ownership</option>
                <option value="selling">Selling / exiting</option>
                <option value="company">Choosing a company</option>
                <option value="unsure">Not sure yet</option>
              </select>
              <textarea
                data-testid="consult-message"
                placeholder="Anything we should know? (optional)"
                value={form.message}
                onChange={set("message")}
                rows={4}
                className={`${inputCls} sm:col-span-2 resize-none`}
              />
              <div className="sm:col-span-2 flex flex-wrap items-center gap-6 mt-2">
                <button
                  data-testid="consult-submit-button"
                  type="submit"
                  disabled={sending}
                  className="group flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full font-semibold text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300"
                >
                  {sending ? "Sending…" : "Request our consultation"}
                  <PaperPlaneRight size={17} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                </button>
                <p className="text-xs text-foreground/45 max-w-[260px]">No spam, no pressure. Your details stay with us.</p>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
